export interface LogoProps {
  className?: string;
  variant?: "full" | "mark";
  /** Which script leads. The other is shown smaller alongside it. */
  script?: "en" | "ar";
  tone?: "color" | "light" | "dark";
}

/**
 * DOSA wordmark — "Broadsheet" mark, per the imported Claude Design project. Concept: a
 * hand-drawn pulse line (a heartbeat trace, standing in for a monitored, well-managed course
 * of treatment) crossing a flat cyan band, with a single magenta dot marking the peak — the
 * moment a pharmacist's attention lands. The Latin "DOSA" and Arabic "دوزا" sit beside the
 * mark in the system's serif/sans pairing, weighted so neither script reads as a translation
 * of the other.
 */
export function Logo({ className, variant = "full", script = "ar", tone = "color" }: LogoProps) {
  const bandFill = tone === "dark" ? "#004961" : "#CBEEFF";
  const strokeColor = tone === "light" ? "#F3F2F2" : "#201E1D";
  const dotFill = "#D6006C";
  const textColor = tone === "light" ? "text-ivory" : tone === "dark" ? "text-charcoal-900" : "text-charcoal-900";

  const mark = (
    <svg width="38" height="24" viewBox="0 0 126 80" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="0" y="26" width="126" height="26" fill={bandFill} />
      <path
        d="M2 74 C 26 74, 34 12, 58 18 C 80 24, 96 58, 124 62"
        fill="none"
        stroke={strokeColor}
        strokeWidth="5"
        strokeLinecap="round"
      />
      <circle cx="58" cy="18" r="7" fill={dotFill} />
    </svg>
  );

  if (variant === "mark") return <span className={className}>{mark}</span>;

  const enWord = (
    <span className={`font-semibold tracking-[-0.005em] ${textColor}`} style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}>
      DOSA
    </span>
  );
  const arWord = (
    <span className={`font-semibold ${textColor}`} style={{ fontFamily: "'IBM Plex Sans Arabic', sans-serif" }} dir="rtl">
      دوزا
    </span>
  );

  return (
    <span className={`inline-flex items-center gap-2.5 ${className ?? ""}`}>
      {mark}
      <span className="flex items-baseline gap-2 text-[1.375rem] leading-none">
        {script === "ar" ? (
          <>
            {arWord}
            <span className="text-xs font-medium text-charcoal-400">{enWord}</span>
          </>
        ) : (
          <>
            {enWord}
            <span className="text-xs font-medium text-charcoal-400">{arWord}</span>
          </>
        )}
      </span>
    </span>
  );
}
